import React, { createContext, useContext, useState, useEffect } from "react";
import { getPublicClinicDetail } from "../../api/clinicServices";
import { useNavigate } from "react-router-dom";

interface ClinicOffice {
  id: number;
  pkid: number;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  phone_number: string;
}

interface ClinicInfo {
  id: number;
  name: string;
  clinic_offices: ClinicOffice[];
}

interface KioskContextType {
  clinicInfo: ClinicInfo | null;
  selectedOfficeId: number | null;
  setSelectedOfficeId: (id: number | null) => void;
  isPublic: boolean;
  loading: boolean;
  error: string | null;
  kioskResponse: any;
  setKioskResponse: (response: any) => void;
  kioskFormData: any;
  setKioskFormData: React.Dispatch<React.SetStateAction<any>>;
}

const KioskContext = createContext<KioskContextType | undefined>(undefined);

export const KioskProvider: React.FC<{ children: React.ReactNode; clinicId?: string }> = ({ children, clinicId }) => {
  const navigate = useNavigate();
  const [clinicInfo, setClinicInfo] = useState<ClinicInfo | null>(null);
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(!!clinicId);
  const [error, setError] = useState<string | null>(null);
  const [kioskResponse, setKioskResponse] = useState<any>(null);
  const [kioskFormData, setKioskFormData] = useState<any>({});

  useEffect(() => {
    const fetchClinic = async () => {
      if (clinicId) {
        setLoading(true);
        try {
          const data = await getPublicClinicDetail(clinicId);
          setClinicInfo(data);
          if (data.clinic_offices?.length === 1) {
            setSelectedOfficeId(data.clinic_offices[0].pkid);
          }
        } catch (err: any) {
          setError(err.message || "");
          navigate("/kiosk/404");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchClinic();
  }, [clinicId]);

  return (
    <KioskContext.Provider
      value={{
        clinicInfo,
        selectedOfficeId,
        setSelectedOfficeId,
        isPublic: !!clinicId,
        loading,
        error,
        kioskResponse,
        setKioskResponse,
        kioskFormData,
        setKioskFormData,
      }}
    >
      {children}
    </KioskContext.Provider>
  );
};

export const useKiosk = () => {
  const context = useContext(KioskContext);
  if (context === undefined) {
    throw new Error("useKiosk must be used within a KioskProvider");
  }
  return context;
};
